/**
 * BudgetEventsBridge — routes Budget Engine events to Editor listeners.
 */

import type {
  BudgetEngine,
  BudgetEvent,
  BudgetEventType,
  BudgetListener,
} from '@/engines/budget'

export class BudgetEventsBridge {
  private unsubscribers: Array<() => void> = []
  private readonly engine: BudgetEngine

  constructor(engine: BudgetEngine) {
    this.engine = engine
  }

  on(type: BudgetEventType | '*', listener: BudgetListener): () => void {
    const off = this.engine.on(type, listener)
    this.unsubscribers.push(off)
    return () => {
      off()
      this.unsubscribers = this.unsubscribers.filter((u) => u !== off)
    }
  }

  /** Convenience alias for document-affecting events. */
  onDocumentChange(listener: (event: BudgetEvent) => void): () => void {
    return this.on('*', (event) => {
      if (event.type === 'budget.command_failed') return
      listener(event)
    })
  }

  onCommandFailed(
    listener: (payload: { code: string; message: string; commandType?: string }) => void,
  ): () => void {
    return this.on('budget.command_failed', (event) => {
      const payload = event.payload as {
        code: string
        message: string
        commandType?: string
      }
      listener(payload)
    })
  }

  disconnectAll(): void {
    for (const off of this.unsubscribers) off()
    this.unsubscribers = []
  }
}
